import { MathUtil } from "@base/math/MathUtil";
import { CacheUtil } from "@base/util/Cache";
import { intern } from "@base/util/Interned";
import { format } from "@base/util/StringFormat";
import { mapRecord, PartialRecord } from "@base/util/TypeUtil";
import { RopeHelper } from "@biz/articles/RopeHelper";
import { CommonLogic, ITransport } from "@biz/CommonLogic";
import { CurrencyData } from "@biz/CurrencyData";
import { Debug } from "@biz/Debug";
import { Access } from "@biz/definitions/AccessDefinitions";
import { ProjectType } from "@biz/definitions/AppDefinitions";
import { getArticleName, IArticle, IRuleArticle, mulArticles, OfferListType, RopeType, RuleArticleMap, setDefaultArticleOfferList } from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureStore } from "@biz/definitions/ArticleFeatureDefinitions";
import { ArticleFeatureLogic } from "@biz/definitions/ArticleFeatureLogic";
import { ArticleFeatureMap } from "@biz/definitions/ArticleFeatureMap";
import { appTemplates, IShopFeatureTemplateId } from "@biz/definitions/ArticleShopTemplates";
import { DiscountGroups, getInflationSurchargePercent, IBeosCompany } from "@biz/definitions/DatabaseDefinitions";
import { IVariantData3d } from "@biz/definitions/IVariantData3d";
import { RemoteChannelCount } from "@biz/definitions/MountDefinitons";
import { ICurrencyData, PriceListIndex } from "@biz/definitions/PriceDefinitions";
import { Product } from "@biz/definitions/Product";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { defaultLanguage, RemoteType } from "@biz/definitions/SunSailDefinitions";
import { ICustomArticle } from "@biz/definitions/UserProfileDefinitions";
import { VariantData3d } from "@biz/definitions/VariantData3d";
import { CalcCurtain2dLogic } from "@biz/items/curtain/CalcCurtain2dLogic";
import { getBizImpl } from "@biz/items/product/BizImpls";
import { CalcStandardCollection2d } from "@biz/items/standardCollection/CalcStandardCollection";
import { CalcSail2dLogic } from "@biz/items/sunSail/CalcSail2dLogic";
import { CalcSail3dLogic } from "@biz/items/sunSail/CalcSail3dLogic";
import { CalcSailLogic } from "@biz/items/sunSail/CalcSailLogic";
import { CalcSCLogic } from "@biz/items/sunSail/CalcSCLogic";
import { ILocaContext } from "@biz/language/LocaContext";
import { AccessHelper } from "@biz/logic/AccessHelper";
import { CalcArticlesHelper } from "@biz/logic/CalcArticlesHelper";
import { IClothData } from "@biz/logic/ClothData";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { Measure } from "@biz/logic/Measure";
import { ProductLogic } from "@biz/logic/ProductLogic";
import { RuleArticleLogic, RuleArticleOptions } from "@biz/logic/RuleArticleLogic";
import { FieldErrorMap } from "@biz/models/FieldErrorMap";
import { INotesAndFilesItem, NotesAndFilesHelper } from "@biz/models/NotesAndFiles";
import { getQuickOverlayPriority, IShoppingArticleNewIdFunction, IShoppingArticleOptions, IShoppingList, IShoppingListContext, IShoppingListItem, IShoppingListItems, ShoppingCartPriority } from "@biz/models/ShoppingListDefinitions";
import { mergeBeosVarianten } from "@biz/orders/BeosVarianten";
import { PD } from "@biz/PD";
import { PriceCalculator } from "@biz/PriceCalculator";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { ProjectCheckErrorHelper } from "@biz/projectCalculation/ProjectCheckErrorHelper";
import {
    ICalcContext, ICalculatedOfferDataResult, ICalculatedProductResult, ICalculatedProjectResult, ICalculatedShoppingCart, IProjectCheckError, IProjectCheckErrorFieldGroup, IProjectCheckErrorOptions, IProjectInfo, IProjectInfoValue, ProductData,
    ProjectCheckErrorPosition, ProjectCheckErrorType, ProjectInfoPriority
} from "@biz/projectCalculation/ProjectDefinitions";
import { OfferCalculationHelper } from "@biz/SSR/OfferCalculationHelper";
import { IColorItem } from "@biz/SunSailColors";
import { IProjectOrderInfo } from "@biz/userProjects/new/IProject";
import { IProjectDealer } from "@biz/userProjects/new/IProjectDefinitions";
import { IVariantData2d, IVariantDataShop, VariantDataType, VariantDataTypes } from "@biz/userProjects/new/IVariant";
import { VariantData2d } from "@biz/userProjects/new/VariantData2d";
import { isValidCommissionName } from "@biz/validityChecks/ValidityChecks";

type FrachtLengthType = "unknown" | "free" | "packet" | 5 | 6;

export class OrderErrorBuilder {
  private error: IProjectCheckError;
  public constructor(error: IProjectCheckError) {
    this.error = error;
  }

  public addFields<T>(_inputModel: T, group: IProjectCheckErrorFieldGroup, ...fields: (keyof T)[]): this {
    return this.addFieldsDirect<T>(group, ...fields);
  }

  public addFieldsDirect<T>(group: IProjectCheckErrorFieldGroup, ...fields: (keyof T)[]): this {
    for (const f of fields) {
      this.error.fields.push({ id: f as string, group });
    }
    return this;
  }
}

export class CalcProductLogic {

  static checkValidClothData(slc: ICalcContext, pd: IProductDescriptor, featureLevel: FeatureLevel, cloth: IClothData | undefined, colorId: string | undefined): boolean {
    let ret = true;
    if (!cloth) {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", slc.lc.t("Error_Cloth_Missing"));
      ret = false;
    } else {
      if (!ClothDataLogic.isValidClothData(pd, featureLevel, cloth)) {
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "net", slc.lc.t("Rule_InvalidClothData"));
        ret = false;
      }
    }

    if (!colorId) {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "net", slc.lc.t("Error_ClothColor_Missing"));
      ret = false;
    }
    return ret;
  }

  static getProductArticles(productId: "shoppingCart" | string, calculatedProject: ICalculatedProjectResult | undefined) {
    const articleMap = productId === "shoppingCart" ? calculatedProject?.shoppingCart?.articles : calculatedProject?.products[productId]?.articles;
    return articleMap;
  }

  static calculateVariantShop(
    data: IVariantDataShop,
    dealer: IProjectDealer,
    orderInfo: IProjectOrderInfo,
    articleMap: Record<string, IArticle>,
    colorMap: Record<string, IColorItem>,
    dealerCompany: IBeosCompany | undefined,
    _customArticles: Record<string, ICustomArticle>,
    lc: ILocaContext,
    currency: ICurrencyData,
    orderTime: number
  ): ICalculatedProjectResult {
    const calculatedOffer = CalcProductLogic.calculateOfferData(dealer, orderInfo, dealerCompany, lc);
    const shoppingCart = CalcProductLogic.calcShoppingArticlesFromShoppingCart("project", data.shoppingList, articleMap, colorMap, dealerCompany, orderInfo.discountOverrides, lc, currency, orderTime);

    const products: Record<string, ICalculatedProductResult> = {};
    const transport = CalcProductLogic.calcTransport(products, shoppingCart, articleMap, dealerCompany, orderInfo.discountOverrides, currency);

    const errors: IProjectCheckError[] = [];
    return {
      type: "shop",
      name: orderInfo.commission,
      products,
      offer: calculatedOffer,
      shoppingCart,
      currency,
      transport,
      errors
    };
  }

  static isCalculatedProjectResultEmpty(cpr: ICalculatedProjectResult): boolean {
    return Object.keys(cpr.products).length === 0 && Object.keys(cpr.shoppingCart.articles).length === 0;
  }

  static calculateVariant(variantData: VariantDataType | undefined, dealer: IProjectDealer, orderInfo: IProjectOrderInfo, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, dealerCompany: IBeosCompany | undefined, customArticles: Record<string, ICustomArticle> | undefined, lc: ILocaContext, currency: ICurrencyData,  orderTime: number): ICalculatedProjectResult {
    const type: ProjectType = variantData?.type || "sail3d";
    const shoppingList = variantData?.shoppingList;
    if (variantData) {
      switch (variantData.type) {
        case "shop":
          return CalcProductLogic.calculateVariantShop(variantData, dealer, orderInfo, articleMap, colorMap, dealerCompany, customArticles || {}, lc, currency, orderTime);
        case "sail2d":
          return CalcProductLogic.calculateVariant2d(variantData, dealer, orderInfo, articleMap, colorMap, dealerCompany, customArticles || {}, lc, currency, orderTime);
        case "sail3d":
          return CalcProductLogic.calculateVariant3d(variantData, dealer, orderInfo, articleMap, colorMap, dealerCompany, customArticles || {}, lc, currency, orderTime);
        default:
          Debug.assert(false, "Not handled so far!");
      }
    }

    const shoppingCart = CalcProductLogic.calcShoppingArticlesFromShoppingCart("project", shoppingList, articleMap, colorMap, dealerCompany, orderInfo.discountOverrides, lc, currency, orderTime);

    const products: Record<string, ICalculatedProductResult> = {};

    const transport = CalcProductLogic.calcTransport(products, shoppingCart, articleMap, dealerCompany, orderInfo.discountOverrides, currency);


    const errors: IProjectCheckError[] = [];

    return {
      type,
      name: orderInfo.commission,
      products,
      shoppingCart,
      offer: CalcProductLogic.calculateOfferData(dealer, orderInfo, dealerCompany, lc),
      currency,
      transport,
      errors
    }
  }

  static calculateVariant2d(
    data: IVariantData2d,
    dealer: IProjectDealer,
    orderInfo: IProjectOrderInfo,
    articleMap: Record<string, IArticle>,
    colorMap: Record<string, IColorItem>,
    dealerCompany: IBeosCompany | undefined,
    customArticles: Record<string, ICustomArticle>,
    lc: ILocaContext,
    currency: ICurrencyData,
    orderTime: number
  ): ICalculatedProjectResult {

    const products: Record<string, ICalculatedProductResult> = {};

    const productRecords = VariantData2d.getProductRecords();
    for (const pr of productRecords) {
      const record = data[pr];
      if (record) {
        const keys = Object.keys(record.byId);
        for (const k of keys) {
          products[k] = CalcProductLogic.calculateProduct(k, record.byId[k], "sail2d", articleMap, colorMap, dealerCompany, customArticles, orderInfo.discountOverrides, lc, currency, orderTime);
        }
      }
    }

    const calculatedOffer = CalcProductLogic.calculateOfferData(dealer, orderInfo, dealerCompany, lc);
    const shoppingCart = CalcProductLogic.calcShoppingArticlesFromShoppingCart("project", data.shoppingList, articleMap, colorMap, dealerCompany, orderInfo.discountOverrides, lc, currency, orderTime);

    const transport = CalcProductLogic.calcTransport(products, shoppingCart, articleMap, dealerCompany, orderInfo.discountOverrides, currency);

    return {
      type: "sail2d",
      name: orderInfo.commission,
      products,
      offer: calculatedOffer,
      shoppingCart,
      currency,
      transport,
      errors: undefined
    };
  }

  static calculateVariant3d(
    data: IVariantData3d,
    dealer: IProjectDealer,
    orderInfo: IProjectOrderInfo,
    articleMap: Record<string, IArticle>,
    colorMap: Record<string, IColorItem>,
    dealerCompany: IBeosCompany | undefined,
    customArticles: Record<string, ICustomArticle>,
    lc: ILocaContext,
    currency: ICurrencyData,
    orderTime: number
  ): ICalculatedProjectResult {

    const globalErrors: IProjectCheckError[] = [];
    if (!dealerCompany)
      CalcProductLogic.addError(globalErrors, ProjectCheckErrorType.FatalError, "project", lc.t("Rule_Dealer_Not_Found"))

    const dataProducts = data ? VariantData3d.getProductsAsRecord(data, { orderableOnly: true }) : {};
    const products: Record<string, ICalculatedProductResult> = mapRecord(dataProducts, (ss, id) => CalcProductLogic.calculateProduct(id, ss, data.type, articleMap, colorMap, dealerCompany, customArticles, orderInfo.discountOverrides, lc, currency, orderTime))


    const slc = CalcProductLogic.calcContextForShoppingCart(articleMap, colorMap, dealerCompany, orderInfo.discountOverrides, lc, currency, "PL0", orderTime);
    CalcProductLogic.addShoppingListArticles(data?.shoppingList, slc);
    const offer = CalcProductLogic.calculateOfferData(dealer, orderInfo, dealerCompany, lc);
    const shoppingCart = CalcProductLogic.finalizeShoppingCart(slc, NotesAndFilesHelper.getNotesAndFilesFromShoppingCart(data?.shoppingList?.notesAndFiles))
    const transport = CalcProductLogic.calcTransport(products, shoppingCart, articleMap, dealerCompany, orderInfo.discountOverrides, currency);

    if (Object.keys(shoppingCart.articles).length === 0 && shoppingCart.notesAndFiles.length > 0) {
      CalcProductLogic.addError(globalErrors, ProjectCheckErrorType.Warning, "project", lc.t("Rule_ShoppingCart_Notes_Empty"))
    }

    { // global Errors
      const soWi = CalcProductLogic.checkRemotesGlobal(products, globalErrors, lc);
      const maxProductCount = 50;
      if (Object.keys(products).length > maxProductCount)
        CalcProductLogic.addError(globalErrors, ProjectCheckErrorType.FatalError, "project", lc.t("Rule_MaxProductCount"), { params: [Object.keys(products).length, maxProductCount]})

      CalcSail3dLogic.checkSnapWinchGlobal(products, globalErrors, lc);
      CalcSail3dLogic.checkSoWiRemoteGlobal(products, shoppingCart, globalErrors, lc, soWi.soWiCentronicConfigurable, soWi.soWiCentronicPlusConfigurable);
    }

    return {
      type: data?.type || "sail3d",
      name: orderInfo.commission,
      products,
      offer,
      shoppingCart,
      currency,
      transport,
      errors: globalErrors
    };
  }

  static #calculateProduct(_cacheId: string, product: ProductData, _type: VariantDataTypes, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, dealerCompany: IBeosCompany | undefined, customArticles: Record<string, ICustomArticle>, discountOverrides: DiscountGroups | undefined, lc: ILocaContext, currency: ICurrencyData, orderTime: number): ICalculatedProductResult {
    const slc = CalcProductLogic.createCalcContext(product, articleMap, colorMap, dealerCompany, customArticles, discountOverrides, lc, currency, "PL0", orderTime);

    const dealerType = AccessHelper.calcDealerTypeFromIndustry(dealerCompany?.industry1)
    if (!PD.isOrderableByDealer(PD.productToPD(product), dealerType))
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", lc.t("Rule_Product_Not_Available_For_Order"))
    if (!product.orderType)
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", lc.t("Rule_Product_NoOrderType"))

    try {
      if (Product.isProductSC(product)) {
        return CalcSCLogic.calculateProduct(product, slc);
      } else if (Product.isProduct2d(product)) {
        switch (product.productType) {
          case "sunSail2d": return CalcSail2dLogic.calc2d(product, slc)
          case "curtain2d": return CalcCurtain2dLogic.calc2d(product, slc)
          case "standardCollection2d": return CalcStandardCollection2d.calc2d(product, slc)
          default:
            throw new Error("ProductType not supported: " + (product as any).productType)
        }
      } else {
        return getBizImpl(product.itemType).calc3d(product, slc);
      }

    } catch (e: any) {
      console.warn("Failed to calculate product", e)
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", "Exception " + e?.message + " " + e?.stack)
      return {
        id: product.id,
        pd: PD.productToPD(product),
        infos: slc.infos,
        errors: slc.errors,
        errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
        articles: slc.ruleArticles,
        // modified: sunSail,
        product: undefined
        // modifiedSunSail: sunSail,
      }
    }
  }

  static calculateProduct = CacheUtil.memo(CalcProductLogic.#calculateProduct, "CalcSailLogic.calculateProduct", {
    idsFromArgs: 1,
    inputsEqualFn: "ref",
    resultEqualFn: "shallow",
  })

  static createCalcContext(product: ProductData | undefined, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, dealerCompany: IBeosCompany | undefined, customArticles: Record<string, ICustomArticle>, discountOverrides: DiscountGroups | undefined, lc: ILocaContext, currency: ICurrencyData, priceListIndex: PriceListIndex, orderTime: number): ICalcContext {
    const slc: ICalcContext = {
      orderTime,
      articleMap,
      colorMap,
      company: {
        surcharge: dealerCompany?.surcharge || 0,
        language: dealerCompany?.language || defaultLanguage,
        countryCode: dealerCompany?.countryCode,
        discountGroups: CommonLogic.calcDiscountGroups(dealerCompany?.discounts, Date.now(), discountOverrides)
      },
      wishList: product?.shoppingList?.items || {},
      powderCoating: product && "powderCoating" in product ? product.powderCoating : undefined,
      customArticles,
      lc,
      currency,
      priceListIndex,

      missingArticles: [],
      ruleArticles: {},
      errors: [],
      infos: [],
    }
    return slc;
  }

  private static _calculateOfferData(dealer: IProjectDealer, orderInfo: IProjectOrderInfo, dealerCompany: IBeosCompany | undefined, lc: ILocaContext): ICalculatedOfferDataResult {
    const errors: IProjectCheckError[] = [];
    if (!dealer.companyId) CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "form", lc.t("Error_Select_Missing_Company")).addFields(dealer, "form", "companyId");
    if (!dealer.userId) CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "form", lc.t("Error_Select_Missing_User")).addFields(dealer, "form", "userId");
    const commissionError = isValidCommissionName(orderInfo.commission, lc);
    if (commissionError)
      CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "form", commissionError).addFields(orderInfo, "form", "commission");


    //     const discounts = CommonLogic.calcDiscountGroups(loggedInCompany?.discounts, Date.now(), calculated?.offer.discountOverrides)
    // const inflationSurchargePercent = getInflationSurchargePercent(loggedInCompany)
    // CommonLogic.calcDiscountGroups(company.discounts, Date.now(), discountOverrides), getInflationSurchargePercent(company),
    return {
      errors,
      errorMap: FieldErrorMap.errorMapFromErrors(errors || []),
      customerSurcharge: orderInfo.customerSurcharge || 0,
      discountOverrides: orderInfo.discountOverrides,
      discountGroups: CommonLogic.calcDiscountGroups(dealerCompany?.discounts, Date.now(), orderInfo.discountOverrides),
      inflationSurchargePercent: getInflationSurchargePercent(dealerCompany),
      orderTime: Date.now()
    };
  }

  static calculateOfferData = CacheUtil.memo(CalcProductLogic._calculateOfferData, "CalcSailLogic.calculateOfferData", {
    idsFromArgs: ["project"],
    inputsEqualFn: "ref",
    resultEqualFn: "shallow",
  })

  static createError(type: ProjectCheckErrorType, position: ProjectCheckErrorPosition, text: string, options?: IProjectCheckErrorOptions): IProjectCheckError {
    text = format(text, options?.params || {});

    const item = ProjectCheckErrorHelper.create(position, text, type, options?.errorUrl);
    return item;
  }

  static addError(errors: IProjectCheckError[], type: ProjectCheckErrorType, position: ProjectCheckErrorPosition, text: string, options?: IProjectCheckErrorOptions): OrderErrorBuilder {
    const item = CalcProductLogic.createError(type, position, text, options);
    errors.push(item);
    return new OrderErrorBuilder(item);
  }

  static addInfo(infos: IProjectInfo[], name: string, value: IProjectInfoValue, options?: { group?: string, priority?: ProjectInfoPriority, access?: Access, maxPerRow?: number }): void {
    const { priority = ProjectInfoPriority.SunSail, access = Access.Monteur, group, maxPerRow = 8 } = options || {};

    infos.push({ name, value, priority, access, group, maxPerRow });
  }

  static addInfoLength(infos: IProjectInfo[], name: string, value: number, options?: { group?: string, priority?: ProjectInfoPriority, access?: Access }): void {
    CalcProductLogic.addInfo(infos, name, Measure.toMM(value), options);
  }

  static addShoppingListArticles(shoppingCart: IShoppingList | undefined, slc: IShoppingListContext): void {
    if (!shoppingCart) {
      return
    }
    const keys = Object.keys(shoppingCart.items);
    for (const k of keys) {
      const sci = shoppingCart.items[k];
      if (sci.count !== undefined && sci.count > 0) {
        const article = slc.articleMap[sci.articleNumber];
        if (!article && sci.articleNumber) {
          slc.missingArticles.push(sci.articleNumber);
        } else {
          const ssa = RuleArticleLogic.createRuleArticleFromArticle(k, article, slc.company.surcharge, {
            idMode: "shopping",
            count: sci.count,
            mul: sci.mul,
            articleType: "optional",
            maxUnits: article.maxUnits,
            features: sci.features,
            priceListIndex: slc.priceListIndex
          });
          const featureTemplate = ArticleFeatureMap.getShopFeatureTemplate(sci.articleNumber);
          if (featureTemplate) {
            const aai = ArticleFeatureLogic.getAdditionalArticleInfoItemsForArticle(featureTemplate, ssa.articleNumber, ssa.features, { colorMap: slc.colorMap, articleMap: slc.articleMap, lc: slc.lc })
            ssa.additionalArticleInfo = { ...ssa.additionalArticleInfo, ...aai }
            const beosVarianten = ArticleFeatureLogic.getAdditionalArticleBeosVarianten(featureTemplate, ssa, { colorMap: slc.colorMap, articleMap: slc.articleMap, lc: slc.lc })
            ssa.beosOrder = { ...ssa.beosOrder, varianten: mergeBeosVarianten(ssa.beosOrder?.varianten, beosVarianten) }
          }
          if (slc.ruleArticles[ssa.id])
            throw new Error("Article already exists... " + ssa.id);
          slc.ruleArticles[ssa.id] = ssa;
        }
      }
    }
  }

  static calcContextForShoppingCart(
    articleMap: Record<string, IArticle>,
    colorMap: Record<string, IColorItem>,
    dealerCompany: IBeosCompany | undefined,
    discountOverrides: DiscountGroups | undefined,
    lc: ILocaContext,
    currency: ICurrencyData,
    priceListIndex: PriceListIndex,
    orderTime: number,
  ): ICalcContext {

    const slc = CalcProductLogic.createCalcContext(undefined, articleMap, colorMap, dealerCompany, {}, discountOverrides, lc, currency, priceListIndex, orderTime);
    return slc;
  }

  static finalizeShoppingCart(slc: ICalcContext, notesAndFiles: INotesAndFilesItem[] = []): ICalculatedShoppingCart {
    CalcProductLogic.finalizeCalcContext(slc, { incomplete: false, defaultOfferList: "H", count: 1 });
    return {
      articles: slc.ruleArticles,
      errors: slc.errors,
      errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
      infos: slc.infos,
      notesAndFiles
    };
  }

  private static _calcShoppingArticlesFromShoppingCart(
    _cacheId: string,
    shoppingCart: IShoppingList | undefined,
    articleMap: Record<string, IArticle>,
    colorMap: Record<string, IColorItem>,
    dealerCompany: IBeosCompany | undefined,
    discountOverrides: DiscountGroups | undefined,
    lc: ILocaContext,
    currency: ICurrencyData,
    orderTime: number
  ): ICalculatedShoppingCart {

    const slc = CalcProductLogic.calcContextForShoppingCart(articleMap, colorMap, dealerCompany, discountOverrides, lc, currency, "PL5", orderTime)
    CalcProductLogic.addShoppingListArticles(shoppingCart, slc);
    return CalcProductLogic.finalizeShoppingCart(slc, NotesAndFilesHelper.getNotesAndFilesFromShoppingCart(shoppingCart?.notesAndFiles));
  }

  static calcShoppingArticlesFromShoppingCart = CacheUtil.memo(CalcProductLogic._calcShoppingArticlesFromShoppingCart, "CalcSailLogic.calcShoppingArticlesFromShoppingCart", {
    idsFromArgs: 1,
    inputsEqualFn: "ref",
    resultEqualFn: "shallow",
  })

  static calcPriceFromIncluded(article: IRuleArticle | undefined, articleMap: Record<string, IArticle>, companySurcharge: number, priceListIndex: PriceListIndex): void {
    const log: boolean = false;
    let total = 0;
    if (article && article.included) {
      const keys = Object.keys(article.included);
      keys.forEach((k, index) => {
        const inc = article.included![k];
        const mul = inc.mul ?? 1;
        const art = articleMap[inc.articleNumber];
        const price = RuleArticleLogic.calcPricePerItem(art, companySurcharge, { priceListIndex, mul, tieBackLength: inc.features?.tieBackLength });
        const buyCount = (inc.count - (inc.countClientAvailable || 0));
        const itemPrice = MathUtil.round(price * buyCount * mul, 2);
        if (log)
          console.log(`Sub: ${index + 1}: ${buyCount.toFixed(4)} x ${mul.toFixed(4)} x ${price.toFixed(4)}  = ${itemPrice.toFixed(4)} (${k})`)
        total += itemPrice
      })
      article.articlePrice = total;
      if (log)
        console.log(`Total: ${total}`)
    }
  }

  static addFlyer(slc: IShoppingListContext): void {
    // E5800085
    const article = "E5800085";
    if (slc.articleMap[article])
      CalcProductLogic.addRuleArticle(slc, article, { count: 1, partList: { list: "sunSailList" }, offerList: "KS" }); // https://plaspack.atlassian.net/browse/APP22-585
  }


  static getWishListItemEnforceOne(wishList: IShoppingListItems, id: string): IShoppingListItem {
    const wishListItem = wishList?.[id];
    return wishListItem;
  }

  static addFracht(slc: IShoppingListContext, type: FrachtLengthType, offerList: OfferListType): void {
    let length: string = "";
    if (type === "unknown") {
      CalcProductLogic.addRuleArticle(slc, "99VERSANDKOSTEN", { count: 1, offerList, partList: { list: "none" }, articleGroup: "end", shoppingCartPriority: ShoppingCartPriority.END });
      return;
    }

    if (type === "free")
      length = "";
    else if (type === "packet")
      length = "2";
    else
      length = "" + type;
    CalcProductLogic.addRuleArticle(slc, "99FRACHT" + length, { count: 1, offerList, partList: { list: "none" }, articleGroup: "end", shoppingCartPriority: ShoppingCartPriority.END }); // Transportzuschlag bis 5 m Länge
  }

  static hasMainArticles(slc: IShoppingListContext, offerListType?: OfferListType): boolean {
    const { ruleArticles: articles } = slc;
    for (const ka of Object.keys(articles)) {
      const a = articles[ka];
      if (CalcArticlesHelper.isArticleToBuyForDealer(a) && (!offerListType || (a.offerList || "H") === offerListType))
        return true;
    }
    return false;
  }

  static checkCalculatedRuleArticles(articles: RuleArticleMap, articleMap: Record<string, IArticle>, errors: IProjectCheckError[], lc: ILocaContext): void {
    for (const k of Object.keys(articles)) {
      const article = articles[k];
      if (article.customArticle)
        continue;
      if (article.count > 0 && article.articleNumber) {
        const a = articleMap[article.articleNumber];
        if (!a) {
          CalcProductLogic.addError(errors, ProjectCheckErrorType.Error, "articleAvailability", format(lc.t("Error_Article_Not_Found"), article.articleNumber))
        } else {
          const av = a.availability;
          const articleText = article.articleNumber + " " + getArticleName(a, lc.languageType) + "";
          switch (av) {
            case "available": break;
            case "unavailable": CalcProductLogic.addError(errors, ProjectCheckErrorType.Error, "articleAvailability", lc.t("Rule_Article_Unavailable"), { params: articleText}); break;
            case "longerDelivery": CalcProductLogic.addError(errors, ProjectCheckErrorType.Hint, "articleAvailability", lc.t("Rule_Article_LongerDelivery"), { params: articleText}); break;
            case "onRequest": CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "articleAvailability", lc.t("Rule_Article_OnRequest"), { params: articleText}); break;
          }
        }
      }
    }
  }

  static addCustomArticles(slc: ICalcContext): void {
    const articles: ICustomArticle[] = [];
    const keys = Object.keys(slc.customArticles);
    for (const k of keys) {
      const ca = slc.customArticles[k];
      articles.push(ca);
    }

    const customMap: Record<string, string> = {};

    const sortedArticles = articles.sort((a, b) => a.priority - b.priority);
    for (const ca of sortedArticles) {
      if (slc.articleMap[ca.articleId])
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", "Der Artikel: " + ca.articleId + " existiert bereits in der Soliday Preisliste und darf nicht verwendet werden. Bitte löschen Sie diesen aus Ihren benutzerdefinierten Artikeln!");
      // CalcProductLogic.addRuleArticle(slc, "#" + ca.articleId, { idPart: ca.id, count: 0, articleType: "optional", quickCategory: "customArticles", maxUnits: 1000, quickOverlayPriority: ca.priority, customArticle: { price: ca.price, name: ca.name, desc: ca.desc, unit: ca.unit } });
      CalcProductLogic.addRuleArticle(slc, "#" + ca.articleId, { idMode: "customArticle", idCategory: ca.id, count: 0, articleType: "optional", quickCategory: "customArticles", quickOverlayPriority: ca.priority, customArticle: { price: ca.price, name: ca.name, desc: ca.desc, unit: ca.unit }, maxUnits: 1000, });

      if (!ca.articleId)
        CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "product", slc.lc.t("Error_CustomArticle_MissingId"));
      else {
        const existingId = customMap[ca.articleId];
        if (existingId && existingId !== ca.id) {
          CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "product", slc.lc.t("Error_CustomArticle_SameId"));
        }
      }
      customMap[ca.articleId] = ca.id;
    }
  }

  static finalizeCalcContext(slc: ICalcContext, options: { incomplete: boolean, defaultOfferList: OfferListType, count: number }): ICalcContext {
    CalcProductLogic.addCustomArticles(slc);

    if (Object.keys(slc.ruleArticles).length > 0) {
      // https://plaspack.atlassian.net/browse/SA-40 // 5.4. doch nicht für shop
      // const article = articleMap["99MANIPUL"];
      // const ssa = CalcSailLogic.createShoppingArticle(article, ShoppingArticleType.Obligatory, surscharge, { count: 1, mul: 1,  partList: { list: "none" }, articleGroup: "end", quickOverlayPriority: QuickOverlayPriority.END });
      // articles.push(ssa)

      // CalcSailLogic.addFracht(slc, "unknown", "S") todo sadf asdf
    }


    // if (Object.keys(slc.articles).length > 0) {
    //   // https://plaspack.atlassian.net/browse/SA-40 // 5.4. doch nicht für shop
    //   // const article = articleMap["99MANIPUL"];
    //   // const ssa = CalcSailLogic.createShoppingArticle(article, ShoppingArticleType.Obligatory, surscharge, { count: 1, mul: 1,  partList: { list: "none" }, articleGroup: "end", quickOverlayPriority: QuickOverlayPriority.END });
    //   // articles.push(ssa)

    //   // CalcSailLogic.addFracht(slc, "unknown", "S") todo sadf asdf
    //   CalcSailLogic.addRuleArticle(slc, { articleNumber: "SOTEUZ", variants: {}, overridePrice: 1 }, { count: 1 });
    // }

    if (options.incomplete)
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.FatalError, "product", slc.lc.t("Error_Product_Incomplete"));

    for (const mc of slc.missingArticles) {
      CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "articleAvailability", format(slc.lc.t("Error_Article_Not_Found"), mc));
    }

    slc.ruleArticles = mapRecord(slc.ruleArticles, (item) => mulArticles(setDefaultArticleOfferList(item, options.defaultOfferList), options.count))

    CalcProductLogic.checkCalculatedRuleArticles(slc.ruleArticles, slc.articleMap, slc.errors, slc.lc);

    // CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "product", "Test");
    return slc
  }

  static calcTransport(products: Record<string, ICalculatedProductResult>, shoppingCart: ICalculatedShoppingCart, articleMap: Record<string, IArticle>, dealerCompany: IBeosCompany | undefined, discountOverrides: DiscountGroups | undefined, currency: ICurrencyData): ITransport | undefined {
    if (dealerCompany?.countryCode !== "A" && dealerCompany?.countryCode !== "D")
      return transportUnknown;

    const customerSurCharge = 0;
    const _discountGroups = CommonLogic.calcDiscountGroups(dealerCompany?.discounts, Date.now(), discountOverrides) || {};
    const _inflationSurcharge = getInflationSurchargePercent(dealerCompany);

    const orderTypeTotal: Record<OfferListType, number> = { S: 0, H: 0, KH: 0, KS: 0, N: 0 };
    const includedOrderTypeTotal: Record<OfferListType, number> = { S: 0, H: 0, KH: 0, KS: 0, N: 0 };
    let onlySailAndRaff = true;

    for (const pk of Object.keys(products)) {
      const p = products[pk];
      if (!PD.isSailOrRaff(p.pd))
        onlySailAndRaff = false;
      const calculatedArticles = OfferCalculationHelper.calculateArticles(p.articles, articleMap, _discountGroups, customerSurCharge, _inflationSurcharge, CurrencyData.EURO, p.product?.orderType || "H");
      for (const ca of calculatedArticles) {
        if (!ca.customArticle) {
          const ot = ca.offerList;
          orderTypeTotal[ot] += ca.ek;
          if ((p.product?.orderType || "").includes(ot))
            includedOrderTypeTotal[ot] += ca.ek;
        }
        // console.log(p.articleId + ": " + p.dealerPrice);
      }
    }

    if (shoppingCart) {
      const calculatedArticles = OfferCalculationHelper.calculateArticles(shoppingCart.articles, articleMap, _discountGroups, customerSurCharge, _inflationSurcharge, CurrencyData.EURO, "H");
      for (const ca of calculatedArticles) {
        if (!ca.customArticle) {
          const ot = ca.offerList;
          orderTypeTotal[ot] += ca.ek;
          includedOrderTypeTotal[ot] += ca.ek;
        }
        // console.log(p.articleId + ": " + p.dealerPrice);
      }
    }

    // console.log("Transport: ", total);

    const totalIncluded = includedOrderTypeTotal["H"] + includedOrderTypeTotal["S"];
    // https://plaspack.atlassian.net/browse/APP-1330
    if (includedOrderTypeTotal["H"] === 0 && onlySailAndRaff) {
      const articleNumber = "99FRACHT2";
      const article = articleMap[articleNumber];

      const basePrice = RuleArticleLogic.calcPricePerItem(article, dealerCompany?.surcharge, { priceListIndex: "PL0" });
      const dealerPrice = PriceCalculator.getDealerPriceDirect(basePrice, _discountGroups[article?.discountGroup]?.discount || 0, false, currency);
      const ek = PriceCalculator.calcAmountPrice(dealerPrice, 1, 1, currency);

      return intern({
        type: "number",
        value: ek,
        articleId: articleNumber
      })
    }

    const transportThreshold = 2500;
    if (Math.round(totalIncluded) >= transportThreshold) { // transport is free
      return undefined;
    }

    return transportUnknown
  }

  static checkRemotesGlobal(products: Record<string, ICalculatedProductResult>, errors: IProjectCheckError[], lc: ILocaContext): {
    soWiCentronicConfigurable: boolean,
    soWiCentronicPlusConfigurable: boolean
  } {
    let soWiCentronicConfigurable = false;
    let soWiCentronicPlusConfigurable = false;
    const remotesChannels: PartialRecord<RemoteType, number> = {};
    {
      const channelsPerType: PartialRecord<RemoteType, RemoteChannelCount> = {};
      for (const k of Object.keys(products)) {
        const product = products[k];
        if (product.product) {
          const remoteChannelInfo = ProductLogic.calcRemoteChannelCount(product.product)
          if (remoteChannelInfo.motorRemoteType) {
            channelsPerType[remoteChannelInfo.motorRemoteType] = ProductLogic.addRemoteChannels(channelsPerType[remoteChannelInfo.motorRemoteType], remoteChannelInfo);

            for (const ka of Object.keys(product.articles)) {
              const article = product.articles[ka];
              const remote = CalcSailLogic.remotes[article.articleNumber];
              if (remote) {
                soWiCentronicConfigurable = soWiCentronicConfigurable || (remote.sunWindSensorConfigurable && remote.type === "centronic" && article.count > 0);
                soWiCentronicPlusConfigurable = soWiCentronicPlusConfigurable || (remote.sunWindSensorConfigurable && remote.type === "centronic+" && article.count > 0)
                const newChannels = remote.channelCount * article.count;
                remotesChannels[remote.type] = (remotesChannels[remote.type] || 0) + newChannels;
              }
            }
          }
        }
      }

      const centronicRequired = channelsPerType.centronic?.totalChannels || 0;
      const centronicAvailable = remotesChannels.centronic || 0;
      const centronicPlusRequired = channelsPerType["centronic+"]?.totalChannels || 0;
      const centronicPlusAvailable = remotesChannels["centronic+"] || 0;

      if (centronicAvailable < centronicRequired) {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "project", lc.t("Rule_Remote_Channels"), { params: [centronicAvailable, centronicRequired]})
      }
      if (centronicAvailable + centronicPlusAvailable < centronicRequired + centronicPlusRequired) {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "project", lc.t("Rule_Remote_Channels"), { params: [centronicAvailable + centronicPlusAvailable, centronicRequired + centronicPlusRequired]})
      }
    }
    return { soWiCentronicConfigurable, soWiCentronicPlusConfigurable };
  }

  static addRopeArticles(ropeTypes: RopeType[], slc: IShoppingListContext, options: Partial<IShoppingArticleOptions>) {
    const ropeArticles = RopeHelper.getRopeArticles(ropeTypes).map((r) => r.id);
    const ropeOptions: Partial<IShoppingArticleOptions> = { stkUnit: "m", ...options }

    CalcProductLogic.addRuleArticle(slc, ropeArticles, ropeOptions); // id spannSeil
  }

  static #calcArticleNumbersArray(articleNumbers: string | string[]): string[] {
    if (Array.isArray(articleNumbers)) {
      return articleNumbers;
    } else {
      const shopFeatureArticles = ArticleFeatureMap.getShopFeatureArticles(articleNumbers as IShopFeatureTemplateId)
      if (shopFeatureArticles?.length)
        return shopFeatureArticles;
      return [articleNumbers];
    }
  }

  static #combineArticleId(...items: (string | undefined)[]): string {
    return items.filter((item) => ![undefined, ""].includes(item)).join("|")
  }
  static calcShopArticleId(articleNumber: string, mul: number | undefined): string {
    const additionalId = RuleArticleLogic.calcIdFromKeyObjectNew({
      mul: mul === 1 ? undefined : mul
    });
    return CalcProductLogic.#combineArticleId(articleNumber, additionalId);
  }

  static #calcAdditionalId(options: (Partial<IShoppingArticleOptions>) | undefined, withPredefinedFeaturesAndMul: boolean): string {
    const additionalId = RuleArticleLogic.calcIdFromKeyObjectNew({
      ...options?.affiliation,
      mul: options?.mul === 1 ? undefined : options?.mul,
      ...(withPredefinedFeaturesAndMul ? options?.features : []),
      overridePrice: options?.overridePrice
    });
    return additionalId;
  }

  static calcGetIdFn(options: (Partial<IShoppingArticleOptions>) | undefined, boxIndex: number): IShoppingArticleNewIdFunction {
    const parentId = options?.parentArticle?.id ? "P" + options.parentArticle.id : "";
    const articleType = options?.articleType === "optional" ? "?" : "";
    const orderType = options?.offerList || "";
    const aot = articleType + orderType;
    const pre = [parentId, aot, options?.idCategory];

    if (options?.idMode === "customArticle") {
      // ignore articleNumber and features - articleId is stored within categoryId
      return (_articleNumber) => CalcProductLogic.#combineArticleId(...pre, CalcProductLogic.#calcAdditionalId(options, false));
    } else if (options?.idMode === "shopping") {
      // for shopping return a very unique id with all feature options and so on...
      return (articleNumber) => CalcProductLogic.#combineArticleId(...pre, articleNumber, CalcProductLogic.#calcAdditionalId(options, true));
    } else {
      let boxIndexString = "";
      if (options?.idMode === "boxed") {
        boxIndexString = boxIndex === 0 ? "" : `[${boxIndex}]`;
      }
      return (articleNumber) => CalcProductLogic.#combineArticleId(...pre, articleNumber + boxIndexString, CalcProductLogic.#calcAdditionalId(options, true));
    }
  }

  static calcGetIdDeprecated(options: (Partial<IShoppingArticleOptions>) | undefined, _boxIndex: number): IShoppingArticleNewIdFunction {
    if (_boxIndex !== 0)
      return () => "____NONE____"
    return (articleNumber) => {
      const mainKey: any = { articleNumber }

      const idKeyObject: any = {
        ...mainKey,
        category: options?.idCategory,
        ...(options?.affiliation || []),
      }

      return CalcProductLogic.calcIdFromKeyObject(idKeyObject);
    }
  }

  static calcIdFromKeyObject(keyObject: any): string {
    const keys = Object.keys(keyObject)
    // normalize idKeyObject
    for (const k of keys) {
      if (keyObject[k] === undefined)
        delete keyObject[k];
    }

    const id = JSON.stringify(keyObject, keys.sort());
    return id;
  }

  static #calcWishListItemIndex(articleNumberArray: string[], getIdFromArticle: IShoppingArticleNewIdFunction, wishList: IShoppingListItems): number {
    for (let i = 0; i < articleNumberArray.length; i++) {
      const articleNumber = articleNumberArray[i];
      const articleId = getIdFromArticle(articleNumber);
      const wishListItem = wishList[articleId];
      if ((wishListItem?.count || 0) > 0) {
        return i;
      }
    }
    return -1;
  }

  static addRuleArticle(
    slc: IShoppingListContext,
    articleNumbers: IShopFeatureTemplateId | string | string[] | undefined,
    options: Partial<IShoppingArticleOptions>
  ): IRuleArticle | undefined {
    if (!articleNumbers)
      return undefined;

    const articleNumberArray = CalcProductLogic.#calcArticleNumbersArray(articleNumbers);
      if (options?.articleType === "obligatory" && articleNumberArray.length > 1) {
        throw new Error("obligatory article must have only one articleNumber");
      }

    const filledBoxes: IRuleArticle[] = [];
    let emptyBox: IRuleArticle | undefined;
    const maxBoxCount = options?.idMode === "boxed" ? 5 : 1;
    let iBox = 0;
    while (iBox < maxBoxCount) {
      const getIdFromArticle = CalcProductLogic.calcGetIdFn(options, iBox);
      const getIdDeprecated = CalcProductLogic.calcGetIdDeprecated(options, iBox);
      const wishListIndex = CalcProductLogic.#calcWishListItemIndex(articleNumberArray, getIdFromArticle, slc.wishList);
      if (wishListIndex !== -1 || !emptyBox) {
        const ret: IRuleArticle[] = [];
        for (const articleNumber of articleNumberArray) {
          const ruleArticle = CalcProductLogic.#createRuleArticleInstance(slc, articleNumber, options, getIdFromArticle, getIdDeprecated);
          if (ruleArticle) {
            ret.push(ruleArticle);
          }
        }

        const selectedRuleArticle = ret[wishListIndex === -1 ? 0 : wishListIndex];
        selectedRuleArticle.featureArticles = ret;

        if (wishListIndex === -1)
          emptyBox = selectedRuleArticle;
        else
          filledBoxes.push(selectedRuleArticle);
      }
      iBox++;
    }

    const sorted = filledBoxes.sort((a, b) => (a.addedSlotTime || Number.MAX_VALUE) - (b.addedSlotTime || Number.MAX_VALUE));
    if (emptyBox)
      sorted.push(emptyBox);
    const merged = sorted.map((a) => CalcProductLogic.#mergeArticleInstance(slc, a, options))
    return merged[0]
  }

  static #mergeArticleInstance(slc: IShoppingListContext, ruleArticle: IRuleArticle, options: Partial<IShoppingArticleOptions>): IRuleArticle {
    let targetRuleList: RuleArticleMap = slc.ruleArticles;
    if (options?.parentArticle) {
      if (!options.parentArticle.included) {
        targetRuleList = {};
        options.parentArticle.included = targetRuleList;
      } else {
        targetRuleList = options.parentArticle.included;
      }
    }

    RuleArticleLogic.assignAdditionalOptions(ruleArticle, options?.additionalArticleInfo, slc) // do before for merging (https://plaspack.atlassian.net/browse/APP-1211)
    const merged = RuleArticleLogic.mergeRuleArticle(ruleArticle, targetRuleList[ruleArticle.id])
    RuleArticleLogic.assignAdditionalOptions(merged, options?.additionalArticleInfo, slc) // do afterwards for correct count, (additional article info functions)

    const optimized = RuleArticleLogic.optimizeRuleArticle(merged);

    targetRuleList[optimized.id] = optimized;
    return merged;
  }

  static #createRuleArticleInstance(slc: IShoppingListContext, articleNumber: string, options: Partial<IShoppingArticleOptions>, getIdFromArticle: IShoppingArticleNewIdFunction, getIdDeprecatedFromArticle: IShoppingArticleNewIdFunction): IRuleArticle | undefined {
      const articleInfo = slc.articleMap[articleNumber];

      if (articleNumber && !articleInfo && !options?.customArticle) {
        slc.missingArticles.push(articleNumber);
      } else {
        const articleId = getIdFromArticle(articleNumber);

        const ruleArticle = RuleArticleLogic.createRuleArticle(articleId, articleNumber, options);

        ruleArticle.maxUnits = options?.maxUnits ?? articleInfo?.maxUnits ?? 0;
        ruleArticle.features = options.features;
        ruleArticle.count = RuleArticleOptions.getCount(options);

        const wishListItem = slc.wishList[articleId] ?? slc.wishList[getIdDeprecatedFromArticle(articleNumber)];
        RuleArticleLogic.handlePowderCoating(slc, ruleArticle, wishListItem);
        RuleArticleLogic.assignWishListItem(ruleArticle, wishListItem);
        if (ruleArticle.quickOverlayPriority === undefined)
          ruleArticle.quickOverlayPriority = getQuickOverlayPriority(ruleArticle.articleNumber)

        const featureTemplate = options?.featureTemplate ?? ArticleFeatureMap.getShopFeatureTemplate(articleNumber);
        let features: ArticleFeatureStore = {};
        if (featureTemplate) {
          const mulHandlerId = ArticleFeatureLogic.getMulHandler(featureTemplate); // set mul to mul feature value
          if (mulHandlerId) {
            features = { ...features, [mulHandlerId]: options?.mul } // if just mul is set (e.g. pylon rope 00E13000107)
          }
          const initialFeatures = ArticleFeatureMap.getInitialArticleFeatures(articleNumber);
          if (initialFeatures)
            features = { ...features, ...initialFeatures };
        }
        if (ruleArticle.features)
          features = { ...features, ...ruleArticle.features };
        if (wishListItem?.features)
          features = { ...features, ...wishListItem.features };
        ruleArticle.features = features;

        ruleArticle.articlePrice = options?.customArticle?.price ?? options?.overridePrice ?? RuleArticleLogic.calcRuleArticlePrice(ruleArticle, articleInfo, slc.priceListIndex, slc.company.surcharge);

        if (featureTemplate) {
          ruleArticle.additionalArticleInfo = { ...ruleArticle.additionalArticleInfo, ...ArticleFeatureLogic.getAdditionalArticleInfoItemsForArticle(featureTemplate, articleNumber, features, slc) }
          const beosVarianten = ArticleFeatureLogic.getAdditionalArticleBeosVarianten(featureTemplate, ruleArticle, slc)
          ruleArticle.beosOrder = { ...ruleArticle.beosOrder, varianten: mergeBeosVarianten(ruleArticle.beosOrder?.varianten, beosVarianten)}
        }

        return ruleArticle;
      }
      return undefined;
  }

  static calcDrahtSeilOptions(length: number, config?: { count?: number, startTerminal?: string; endTerminal?: string }, options?: Partial<IShoppingArticleOptions>): Partial<IShoppingArticleOptions> {
    const count = config?.count || 1;
    const features: ArticleFeatureStore = { length };
    if (config?.startTerminal)
      features.startTerminal = config.startTerminal;
    if (config?.endTerminal)
      features.endTerminal = config.endTerminal;
    return {
      featureTemplate: appTemplates.drahtSeil,
      features,
      count, mul: length, stkUnit: "Stk", mulUnit: "lfm", articleType: "obligatory", ...options
    }
  }

  static addDrahtseil(slc: ICalcContext, length: number, diameter: 3 | 4, config?: { count?: number, startTerminal?: string; endTerminal?: string }, options?: Partial<IShoppingArticleOptions>): void {
    CalcProductLogic.addRuleArticle(slc, diameter === 4 ? "00E13000067" : "00E13000255", CalcProductLogic.calcDrahtSeilOptions(length, config, options)); // 00E13000255?

    if (config?.startTerminal)
      CalcProductLogic.addRuleArticle(slc, config.startTerminal, { count: config?.count || 1, articleType: "obligatory", ...options });
    if (config?.endTerminal)
      CalcProductLogic.addRuleArticle(slc, config.endTerminal, { count: config?.count ||  1, articleType: "obligatory", ...options });
  }
}

const transportUnknown: ITransport = intern({
  type: "unknown",
  value: 0,
  articleId: "99VERSANDKOSTEN"
})

  // static addRuleArticleOld(
  //   slc: IShoppingListContext,
  //   article: IRuleArticleVariantInput | string | undefined,
  //   options?: Partial<IShoppingArticleOptionsOld>
  // ): IRuleArticle[] | undefined {
  //   if (!article)
  //     return undefined;

  //   const articleVariantInput: IRuleArticleVariantInput = typeof article === "string" ? { articleNumber: article} : article;
  //   const mainId = RuleArticleLogic.calcIdFromOptions({ articleNumber: articleVariantInput.articleNumber, overridePrice: articleVariantInput.overridePrice, options });

  //   const ret: IRuleArticle[] = [];
  //   const wishListItem = slc.wishList[mainId];

  //   {
  //     const articleNumber = articleVariantInput.articleNumber;
  //     const articleVariant = CalcProductLogic.createArticleVariantsSlc(slc, [{ ...articleVariantInput/*, articleNumber*/ }], options)[0];
  //     const av = { ...articleVariant }
  //     // if (selectedArticle)
  //     //  av.articleNumber = selectedArticle;
  //     const mu = slc.articleMap[articleNumber]?.maxUnits || 0;

  //     const ssa = RuleArticleLogic.createRuleArticle(mainId, av, mu, options);
  //     ssa.features = wishListItem?.features;
  //     RuleArticleLogic.handlePowderCoating(slc, ssa, wishListItem);
  //     RuleArticleLogic.assignWishListItem(ssa, wishListItem);
  //     RuleArticleLogic.assignQuickOverlayPriority(ssa, ssa.articleNumber);

  //     const merged = RuleArticleLogic.mergeAndAssignAdditional(ssa, slc.ruleArticles[mainId], slc, options?.additionalArticleInfo);
  //     slc.ruleArticles[mainId] = merged;
  //     ret.push(merged);
  //   }

  //   // for (const wishListItem of wishListItems) {
  //   //   if (wishListItem.id !== mainId) {

  //   //     const id = wishListItem.id || mainId; // `${mainId}_I${i + 1}`;

  //   //     const articleNumber = articleVariantInput.articleNumber;
  //   //     const articleVariant = CalcProductLogic.createArticleVariantsSlc(slc, [{ ...articleVariantInput, articleNumber }], options)[0];

  //   //     const mu = slc.articleMap[articleNumber]?.maxUnits || 0;
  //   //     const ssa = RuleArticleLogic.createRuleArticle(id, articleVariant, mu, options);

  //   //     ssa.features = wishListItem?.features;
  //   //     RuleArticleLogic.handlePowderCoating(slc, ssa, wishListItem);
  //   //     RuleArticleLogic.assignWishListItem(ssa, wishListItem);
  //   //     RuleArticleLogic.assignQuickOverlayPriority(ssa, articleNumber);

  //   //     if (ssa.articleType === "optional")
  //   //       ssa.articleType = "optionalVariation";

  //   //     const merged = RuleArticleLogic.mergeAndAssignAdditional(ssa, slc.articles[id], slc, options?.additionalArticleInfo, options);
  //   //     slc.articles[id] = merged;

  //   //     // if (articleNumber)
  //   //     //   slc.articles[id].articleNumber = articleNumber;

  //   //     ret.push(merged);
  //   //   }
  //   // }

  //   return ret;
  // }